import React, { useState, useEffect, useRef } from 'react';
import { Button, Typography, Modal, Select, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
    InstagramOutlined,
    FacebookOutlined,
    CalendarOutlined,
    LinkOutlined,
    WhatsAppOutlined,
    PlayCircleFilled,
    RightOutlined,
    CheckCircleFilled,
    GiftOutlined,
    CrownOutlined,
    ClockCircleOutlined,
    DollarOutlined
} from '@ant-design/icons';
import Sider from '../../comps/sider/main';
import callAPI from '../../comps/fetch/main';
import ShowPlans from '../../comps/show_plans/main';
import NewYearSaleBanner from '../../comps/newYearSaleBanner/main';
import './main.css';

const { Title, Text } = Typography;

function WelcomeOnboardingPage() {
    const navigate = useNavigate();
    const [videoModal, setVideoModal] = useState({ open: false, tutorial: null });
    const [language, setLanguage] = useState(localStorage.getItem('preferredLanguage') || 'English');
    const scrollContainerRef = useRef(null);

    const languages = [
        'English', 'Tamil', 'Telugu', 'Malayalam', 'Kannada', 'Hindi',
        'French', 'Brazilian', 'Indonesian', 'Spanish'
    ];

    const handleLanguageChange = (value) => {
        setLanguage(value);
        localStorage.setItem('preferredLanguage', value);
    };
    const [userName, setUserName] = useState('');

    // Greetings in different languages
    const greetings = [
        'Vanakkam',      // Tamil
        'Namaste',       // Hindi
        'Hello',         // English
        'Hola',          // Spanish
        'Bonjour',       // French
        'Ciao',          // Italian
        'Hallo',         // German
        'Olá',           // Portuguese
        'Konnichiwa',    // Japanese
        'Annyeong',      // Korean
        'Merhaba',       // Turkish
        'Halo',          // Indonesian
    ];
    const [greeting] = useState(() => greetings[Math.floor(Math.random() * greetings.length)]);

    // Trial state
    const [userPlan, setUserPlan] = useState(null);
    const [userPlanLoading, setUserPlanLoading] = useState(true);
    const [trialDaysLeft, setTrialDaysLeft] = useState(7);
    const [isOnTrial, setIsOnTrial] = useState(true);
    const [isFreePlan, setIsFreePlan] = useState(true);
    const [daysSinceCreation, setDaysSinceCreation] = useState(0);

    // Plan modal state
    const [showPlanModal, setShowPlanModal] = useState(false);

    useEffect(() => {
        // Check if user is authenticated
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/login');
            return;
        }

        // Get user name from localStorage
        try {
            const userData = JSON.parse(localStorage.getItem('userData'));
            if (userData?.name) {
                setUserName(userData.name.split(' ')[0]); // Get first name
            } else if (userData?.username) {
                setUserName(userData.username);
            }
        } catch (e) {
            console.log('Error parsing user data');
        }

        // Fetch user plan data
        callAPI({ url: 'user_plan', method: 'GET', loadingState: setUserPlanLoading, dataState: setUserPlan });
    }, [navigate]);

    // Calculate trial days when user plan data is loaded
    useEffect(() => {
        if (!userPlan?.data) return;

        const planData = userPlan.data;
        const currentPlanId = planData.plan_details?.id;

        // Check if user is on free plan
        setIsFreePlan(currentPlanId === 'free');

        // Calculate days since account creation with error handling
        let calculatedDaysSinceCreation = 60; // Default to 60 days (no trial) on error

        try {
            if (planData.account_created) {
                const accountCreated = new Date(planData.account_created);

                // Check if date is valid
                if (!isNaN(accountCreated.getTime())) {
                    const now = new Date();
                    const differenceInTime = now.getTime() - accountCreated.getTime();
                    calculatedDaysSinceCreation = Math.floor(differenceInTime / (1000 * 3600 * 24));

                    // Sanity check: if negative or unreasonably large, default to 60
                    if (calculatedDaysSinceCreation < 0 || calculatedDaysSinceCreation > 3650) {
                        calculatedDaysSinceCreation = 60;
                    }
                }
            }
        } catch (error) {
            console.error('Error calculating days since creation:', error);
            calculatedDaysSinceCreation = 60; // Default to no trial on error
        }

        setDaysSinceCreation(calculatedDaysSinceCreation);

        // Check if still in trial period (less than 8 days old)
        if (calculatedDaysSinceCreation < 8 && currentPlanId === 'free') {
            setIsOnTrial(true);
            setTrialDaysLeft(7 - calculatedDaysSinceCreation);

            // Set the temporary plan ID for trial users
            localStorage.setItem('og_planID', currentPlanId);
            localStorage.setItem('planid', '428337'); // Pro plan ID for trial
        } else {
            setIsOnTrial(false);
            setTrialDaysLeft(0);
        }
    }, [userPlan]);

    const tutorials = [
        {
            id: 1,
            title: 'Single Post/Reel Automation',
            description: 'Automate comments to DM on a specific post or reel',
            gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            videoUrls: {
                English: 'https://www.youtube.com/embed/YOUR_VIDEO_ID_1'
            },
            duration: '3:45'
        },
        {
            id: 2,
            title: 'All Posts/Reels Automation',
            description: 'Set up automation for all your content at once',
            gradient: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
            videoUrls: {
                English: 'https://www.youtube.com/embed/YOUR_VIDEO_ID_2'
            },
            duration: '5:20'
        },
        {
            id: 3,
            title: 'DM & Flow Automation',
            description: 'Messages and Manychat-style conversational flows',
            gradient: 'linear-gradient(135deg, #fc4a1a 0%, #f7b733 100%)',
            videoUrls: {
                English: 'https://www.youtube.com/embed/YOUR_VIDEO_ID_3'
            },
            duration: '7:15'
        },
        {
            id: 8,
            title: 'Setup Next Post Automation',
            description: 'Automate comments to DM on a specific post or reel',
            gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            videoUrls: {
                English: 'https://www.youtube.com/embed/YOUR_VIDEO_ID_1'
            },
            duration: '3:45'
        },
        {
            id: 8,
            title: 'Sechedule Future Posts with Comment Automation',
            description: 'Automate comments to DM on a specific post or reel',
            gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            videoUrls: {
                English: 'https://www.youtube.com/embed/YOUR_VIDEO_ID_1'
            },
            duration: '3:45'
        },
        {
            id: 4,
            title: 'How to Connect Instagram Account',
            description: 'Automate comments to DM on a specific post or reel',
            gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            videoUrls: {
                English: 'https://www.youtube.com/embed/YOUR_VIDEO_ID_1'
            },
            duration: '3:45'
        },
        {
            id: 5,
            title: 'How to Connect Facebook Account',
            description: 'Automate comments to DM on a specific post or reel',
            gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            videoUrls: {
                English: 'https://www.youtube.com/embed/YOUR_VIDEO_ID_1'
            },
            duration: '3:45'
        },
        {
            id: 6,
            title: 'How to Connect Shopify Store',
            description: 'Automate comments to DM on a specific post or reel',
            gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            videoUrls: {
                English: 'https://www.youtube.com/embed/YOUR_VIDEO_ID_1'
            },
            duration: '3:45'
        },
        {
            id: 7,
            title: 'Facebook Automation',
            description: 'Automate comments to DM on a specific post or reel',
            gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            videoUrls: {
                English: 'https://www.youtube.com/embed/YOUR_VIDEO_ID_1'
            },
            duration: '3:45'
        },
        {
            id: 9,
            title: 'Instant Open - Open In App tool',
            description: 'Automate comments to DM on a specific post or reel',
            gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            videoUrls: {
                English: 'https://www.youtube.com/embed/YOUR_VIDEO_ID_1'
            },
            duration: '3:45'
        },
        {
            id: 100,
            title: 'InstantDM Platform Tour',
            description: 'Automate comments to DM on a specific post or reel',
            gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            videoUrls: {
                English: 'https://www.youtube.com/embed/YOUR_VIDEO_ID_1'
            },
            duration: '3:45'
        },
    ];

    const features = [
        {
            key: 'instagram',
            icon: <InstagramOutlined />,
            title: 'Instagram Automation',
            description: 'Comment to DM automation for Instagram posts & reels',
            gradient: '#E1306C', // Instagram pink
            path: '/'
        },
        {
            key: 'facebook',
            icon: <FacebookOutlined />,
            title: 'Facebook Automation',
            description: 'Automate Facebook page comments to Messenger',
            gradient: '#1877F2', // Facebook blue
            path: '/fb'
        },
        {
            key: 'later',
            icon: <CalendarOutlined />,
            title: 'Later - Schedule Posts',
            description: 'Schedule and publish content to Instagram',
            gradient: '#7C3AED', // Purple
            path: '/later',
            badge: 'New'
        },
        {
            key: 'oia',
            icon: <LinkOutlined />,
            title: 'Open In App',
            description: 'Smart links that open directly in apps',
            gradient: '#0EA5E9', // Sky blue
            path: '/oia'
        },
        {
            key: 'referral',
            icon: <DollarOutlined />,
            title: 'Referral Dashboard',
            description: 'Earn up to 50% commission for every referral',
            gradient: '#F59E0B', // Amber
            path: '/referral-dashboard'
        }
    ];

    const [paymentLinkData, setPaymentLinkData] = useState()
    const [paymentLinkLoading, setPaymentLinkLoading] = useState()
    const [showINR, setShowINR] = useState(false)
    const [showWaitingPaymentUpdate, setShowWaitingPaymentUpdate] = useState(false)
    const [showWaitingPaymentFailed, setShowWaitingPaymentFailed] = useState(false)
    const [messageApi, contextHolder] = message.useMessage();

    // Determine currency based on timezone
    useEffect(() => {
        const user_tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        setShowINR(user_tz === "Asia/Kolkata" || user_tz === "Asia/Calcutta");
    }, []);

    // Payment processing logic
    useEffect(() => {
        if (paymentLinkData?.open_url) {
            window.location.href = paymentLinkData?.payment_url
            return
        }

        if (paymentLinkData?.payment_url == undefined) {
            return
        }

        messageApi.destroy("payment-process")

        if (!showINR && window.LemonSqueezy) {
            const checkoutUrl = paymentLinkData.payment_url
            window.LemonSqueezy.Url.Open(checkoutUrl);
        }

        if (showINR && window.Razorpay) {
            try {
                const options = {
                    key: "rzp_live_JtjGCHjkESNzIZ",
                    name: "InstantDM",
                    description: "Affordable Comment to DM automation tool",
                    subscription_id: paymentLinkData.rzpy_sub_id,
                    handler: function (response) {
                        setShowPlanModal(false)
                        setShowWaitingPaymentUpdate(true)

                        message.success("Payment completed, Please wait while we're verfiying!")
                        let user_detail = JSON.parse(localStorage.getItem("userData"))
                        let igid = localStorage.getItem("ig_id")

                        const BOT_TOKEN = "7109212238:AAEsaFubA66rs0Ib6rgn5K2amKO9rnOkUv0";
                        const CHAT_ID = '776956029';
                        const url = `https://api.telegram.org/bot${BOT_TOKEN}/sendMessage`;
                        let tele_message = `[SUBSCRIBED PLAN] - user ${user_detail?.emailid}  - Subscribed for  , IG ID = ${igid} , user id = ${user_detail?.user_id}`
                        let data = {
                            chat_id: CHAT_ID,
                            text: tele_message
                        };

                        fetch(url, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json'
                            },
                            body: JSON.stringify(data)
                        })
                            .then(response => response.json())
                            .then(data => {
                                console.log('Message sent successfully:', data);
                            })
                            .catch(error => {
                                console.error('Error sending message:', error);
                            });
                        setPaymentLinkData({})
                    },
                    error: function (response) {
                        setShowPlanModal(false)
                        setShowWaitingPaymentFailed(true)
                        setPaymentLinkData({})
                    },
                    prefill: {
                        name: paymentLinkData.customer_name,
                        email: paymentLinkData.customer_email,
                    },
                    "notes": paymentLinkData.notes != undefined ? { ...paymentLinkData.notes } : {},
                    theme: {
                        color: "#1677ff",
                    },
                    display: {
                        handler: function () {
                            return {
                                "offers": [
                                    {
                                        "type": "discount",
                                        "description": "10% discount on first purchase",
                                        "amount": 100
                                    },
                                    {
                                        "type": "coupon",
                                        "description": "Rs. 100 off on total bill",
                                        "code": "INSTANT10"
                                    }
                                ]
                            }
                        }
                    },
                };

                const razorpayInstance = new window.Razorpay(options);
                razorpayInstance.open();

            } catch (error) {
                alert("Error loading Razorpay: " + error.message);
            }
        } else if (!showINR && !window.LemonSqueezy) {
            // Fallback if LemonSqueezy is not loaded although it should be
            // message.error("Payment provider not loaded. Please refresh.");
        }
    }, [paymentLinkData, showINR, messageApi]);

    const handleFeatureClick = (feature) => {
        navigate(feature.path);
    };

    const openVideo = (tutorial) => {
        setVideoModal({ open: true, tutorial });
    };

    const openWhatsApp = () => {
        window.open('https://wa.me/919876543210?text=Hi, I need help setting up InstantDM', '_blank');
    };

    return (
        <div className="page-layout">
            {contextHolder}
            <Sider />
            <div className="page-content welcome-home-page">
                {/* New Year Sale Banner */}
                <NewYearSaleBanner enableDismiss={true} enableScratch={true} onBuyNowClick={() => setShowPlanModal(true)} />

                {/* Header Section */}
                <div className="welcome-header">
                    <div className="welcome-header-content">
                        <Title level={2} className="welcome-title">
                            {greeting}{userName ? `, ${userName}` : ''}! 👋
                        </Title>
                        <Text className="welcome-subtitle">
                            Facebook + Instagram Comment, stories, DM Automation
                        </Text>
                    </div>
                </div>

                {/* Free Trial Banner - Only show for free plan users */}
                {isFreePlan && (
                    <div className={`free-trial-banner ${!isOnTrial ? 'trial-expired' : ''}`}>
                        <div className="trial-icon">
                            {isOnTrial ? <GiftOutlined /> : <ClockCircleOutlined />}
                        </div>
                        <div className="trial-content">
                            <div className="trial-badge">
                                <CrownOutlined /> {isOnTrial ? 'PRO PLAN TRIAL' : 'FREE PLAN'}
                            </div>
                            {isOnTrial ? (
                                <>
                                    <h3>{trialDaysLeft > 0 ? `${trialDaysLeft} days left` : 'Last day'} of your Pro trial</h3>
                                    <p>Enjoying premium features no credit card required. {trialDaysLeft > 0 ? `Reverts to Free plan in ${trialDaysLeft} days.` : 'Reverts to Free plan tomorrow.'}</p>
                                </>
                            ) : (
                                <>
                                    <h3>Your free trial has ended</h3>
                                    <p>Upgrade to Pro for unlimited automations and premium features.</p>
                                </>
                            )}
                        </div>
                        <div className="trial-actions">
                            <Button type="default" className="upgrade-btn" onClick={() => setShowPlanModal(true)}>
                                {!isOnTrial ? 'Upgrade Now' : 'Show Plans'}
                            </Button>
                        </div>
                    </div>
                )}

                {/* Reusable Sections */}
                {(() => {
                    // Features Section Component
                    const FeaturesSection = (
                        <div className="welcome-section" key="features">
                            <div className="section-header">
                                <Title level={3} className="section-title">
                                    What you want to do now?
                                </Title>
                                <Text className="section-subtitle">
                                    Instagram Automatioon, Facebook Automation or Pick from below.
                                </Text>
                            </div>

                            <div className="features-grid">
                                {features.map((feature) => (
                                    <div
                                        key={feature.key}
                                        className="feature-card"
                                        onClick={() => handleFeatureClick(feature)}
                                    >
                                        <div
                                            className="feature-icon"
                                            style={{ background: feature.gradient }}
                                        >
                                            {feature.icon}
                                        </div>
                                        <div className="feature-content">
                                            <div className="feature-title-row">
                                                <h4>{feature.title}</h4>
                                                {feature.badge && (
                                                    <span className="feature-badge">{feature.badge}</span>
                                                )}
                                            </div>
                                            <p>{feature.description}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );

                    return [FeaturesSection];
                })()}

            </div>

            {/* Video Modal */}
            <Modal
                title={videoModal.tutorial?.title}
                open={videoModal.open}
                onCancel={() => setVideoModal({ open: false, tutorial: null })}
                footer={null}
                width={800}
                centered
                destroyOnClose
                bodyStyle={{ padding: 0 }} /* Remove default padding for full width language bar */
            >
                <div
                    className="language-picker-container"
                    ref={scrollContainerRef}
                    onWheel={(e) => {
                        if (e.deltaY !== 0) {
                            scrollContainerRef.current.scrollLeft += e.deltaY;
                        }
                    }}
                >
                    {languages.map((lang) => (
                        <div
                            key={lang}
                            className={`language-chip ${language === lang ? 'active' : ''}`}
                            onClick={() => handleLanguageChange(lang)}
                        >
                            {lang}
                        </div>
                    ))}
                </div>
                <div className="video-container">
                    <iframe
                        width="100%"
                        height="450"
                        src={videoModal.tutorial?.videoUrls?.[language] || videoModal.tutorial?.videoUrls?.['English']}
                        title={videoModal.tutorial?.title}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                    />
                </div>
            </Modal>

            {/* Plan Modal */}
            <Modal
                title="Plans"
                open={showPlanModal}
                onCancel={() => setShowPlanModal(false)}
                footer={null}
                width='100%'
            >
                <ShowPlans
                    currentPlan={localStorage.getItem("planid")}
                    messageApi={messageApi}
                    setPaymentLinkLoading={setPaymentLinkLoading}
                    setPaymentLinkData={setPaymentLinkData}
                />
            </Modal>
        </div>
    );
}

export default WelcomeOnboardingPage;
